// Shore Doors Locksmiths — pivot content layer (see SHORE_DOORS_PIVOT_PLAN.md)
//
// locksmithLive: true (Hugh, 5 Jul 2026) — the site presents the full locksmith
// service range from day one. Setting it false re-gates the locksmith-dependent
// pages (noindex + "launching soon" banners + unlinked hub cards) if ever needed.

export const siteConfig = {
  brandName: "Shore Doors Locksmiths",
  // Hugh 5 Jul 2026: site presents the full service range from day one
  locksmithLive: true,
  hoursLine: "Mon–Fri 7:00am – 6:00pm",
  afterHoursLine: "Overnight call-outs coming as we grow",
  residentialArea: "Auckland's North Shore — wider Auckland by arrangement",
  commercialArea: "Greater Auckland",
};

// Rate card (plan §1 D7) — confirmed by Hugh 5 Jul 2026.
// GST-EXCLUSIVE: every figure below is plus GST. GST is added at the end of the sale
// (owner ruling 2026-08-22). Any copy quoting these numbers must say "+ GST".
export const rates = {
  callout: 120, // includes travel + first 15 minutes on site
  block15: 30,
  rekeyPerLockFrom: 60,
  keyCutFrom: 15,
  keyCutTo: 25,
};

export const fixedQuoteLine =
  "Fixed quote confirmed before any extra work starts — no surprises.";

export const residentialFaqs = [
  {
    id: "r1",
    question: "How much does a locksmith visit cost?",
    answer:
      `Our standard call-out is $${rates.callout} + GST, which covers travel and the first 15 minutes on site. After that, time is charged at $${rates.block15} + GST per 15-minute block, and we confirm a fixed quote with you before any extra work starts. Rekeying is from $${rates.rekeyPerLockFrom} + GST per lock on top of the call-out.`,
  },
  {
    id: "r2",
    question: "Should I rekey my locks or replace them?",
    answer:
      "If your locks are in good condition and you just need old keys to stop working — after moving house, losing keys, or a tenancy change — rekeying is usually the cheaper fix. If the lock itself is worn, sticking, or outdated, replacement is the better long-term option. We'll tell you straight which one you actually need.",
  },
  {
    id: "r3",
    question: "Do you have a shop I can come to?",
    answer:
      "No — we're a mobile service. Our vans come to you, fully equipped for on-site work across the North Shore and wider Auckland. That's how we keep response simple and pricing clear.",
  },
  {
    id: "r5",
    question: "Do you do car keys or safes?",
    answer:
      "No car keys — automotive keys need specialist machinery, so we leave those to the car-key crowd. Safes, yes: we supply, position and bolt down home and business safes properly. (We don't open or service safe locking mechanisms.)",
  },
  {
    id: "r4",
    question: "What hours do you operate?",
    answer:
      `${siteConfig.hoursLine}. We're building towards overnight emergency call-outs as the team grows — for now, call during business hours and we'll get to you as fast as we can.`,
  },
];

// ---------------------------------------------------------------------------
// Service pages. gated: true = requires the qualified locksmith (plan ⛔ pages):
// built now, published at Phase B via siteConfig.locksmithLive.
// ---------------------------------------------------------------------------

const callCta = { type: "call", label: "Call now" };
const quoteCta = { type: "quote", label: "Request a quote" };

export const servicePages = {
  residential: {
    "lock-rekeying": {
      slug: "lock-rekeying",
      gated: true,
      title: "Lock Rekeying",
      metaTitle: "Lock Rekeying North Shore | Shore Doors Locksmiths",
      metaDescription:
        "Rekey your locks so old keys stop working — after moving house, lost keys or a tenancy change. Mobile locksmiths, North Shore Auckland. From $60 + GST per lock.",
      heroSub:
        "Make old keys useless without replacing your locks. Ideal after moving house, losing keys, or a change of tenants or flatmates.",
      intro:
        "Rekeying changes the pins inside your existing lock so it works with a brand-new key — and nothing else. It's usually faster and cheaper than replacing hardware that's still in good condition, and we can key multiple doors alike so one key runs the whole house.",
      scopeTitle: "When rekeying is the right call",
      scope: [
        "You've just moved into a new home and don't know who has keys",
        "Keys lost or stolen — or not returned after a tenancy",
        "After a break-in, alongside any repairs needed",
        "Keying your doors alike so one key opens everything",
      ],
      showRates: true,
      priceNote: `Rekeying from $${rates.rekeyPerLockFrom} per lock + call-out.`,
      image: "/images/work/mortice-thumbturn-wooden-door.jpeg",
      imageAlt: "Mortice lock with handle and thumb turn fitted to a wooden door",
      primaryCta: callCta,
      crossLinks: [
        { to: "/residential/lock-repair-replacement", label: "Lock repair & replacement" },
        { to: "/residential/home-security-upgrades", label: "Home security upgrades" },
      ],
    },
    "lock-repair-replacement": {
      slug: "lock-repair-replacement",
      gated: false,
      title: "Lock Repair & Replacement",
      metaTitle: "Lock Repair & Replacement North Shore | Shore Doors Locksmiths",
      metaDescription:
        "Sticking, jammed or broken locks repaired or replaced by commercial-grade installers. Mobile service across the North Shore and Auckland. Fixed quotes.",
      heroSub:
        "Sticking, jammed, worn or broken locks — repaired where possible, replaced with quality hardware where not. The same installers trusted on hospitals and banks, at your front door.",
      intro:
        "Our team fits and services commercial-grade door hardware every working day. For your home that means locks that are chosen properly, fitted properly, and still working smoothly years later — not a handyman guess. We supply and fit trusted brands, sort door alignment and latch problems while we're there, and back the work with a 12-month workmanship guarantee.",
      scopeTitle: "What we repair and replace",
      scope: [
        "Entrance sets, deadbolts and mortice locks",
        "Sliding and ranchslider door locks",
        "Window catches and locks",
        "Handles, latches and hinges — plus door alignment fixes",
        "Upgrades to higher-security or smart hardware",
      ],
      showRates: true,
      image: "/images/work/cavity-slider-face.jpeg",
      imageAlt: "Residential cavity slider with recessed flush pull and lock, installed by Shore Doors",
      primaryCta: callCta,
      crossLinks: [
        { to: "/residential/home-security-upgrades", label: "Home security upgrades" },
        { to: "/residential/lock-rekeying", label: "Lock rekeying" },
      ],
    },
    "key-cutting": {
      slug: "key-cutting",
      gated: true,
      title: "Key Cutting",
      metaTitle: "Mobile Key Cutting North Shore | Shore Doors Locksmiths",
      metaDescription:
        "Keys cut at your place from our mobile van — house, window and padlock keys, keyed-alike options. North Shore Auckland locksmiths.",
      heroSub:
        "Keys cut on the spot from our fully equipped van — at your home, not a mall kiosk.",
      intro:
        "We cut house, window and padlock keys on site, so you get spares that actually work, cut from the lock rather than a worn copy where possible. Need one key for several doors? Ask about keying alike while we're there. One thing we don't do: car keys — they need specialist automotive machinery.",
      scopeTitle: "What we cut",
      scope: [
        "House and entrance-door keys",
        "Window and patio-door keys",
        "Padlock keys",
        "Spare sets cut from the lock for accuracy",
      ],
      showRates: true,
      priceNote: `Standard keys $${rates.keyCutFrom}–$${rates.keyCutTo} each on an existing visit. Restricted or high-security keys belong to a key system — see Master Key Systems.`,
      primaryCta: callCta,
      crossLinks: [
        { to: "/commercial/master-key-systems", label: "Master key systems" },
        { to: "/residential/lock-rekeying", label: "Lock rekeying" },
      ],
    },
    "home-security-upgrades": {
      slug: "home-security-upgrades",
      gated: false,
      title: "Home Security Upgrades",
      metaTitle: "Home Security Upgrades North Shore | Shore Doors Locksmiths",
      metaDescription:
        "Deadlocks, window locks, sliding-door locks and smart locks — supplied and fitted by professional installers. North Shore & Auckland mobile service.",
      heroSub:
        "Practical security that actually gets used: deadlocks, window locks, sliding-door hardware and smart locks — supplied, fitted and set up properly.",
      intro:
        "We install electronic locking hardware in commercial buildings for a living, so a smart lock or a full set of window locks at your place is comfortably inside the day job. We'll walk your home's entry points with you, recommend what's worth doing (and what isn't), and fit it to insurance-friendly standard.",
      scopeTitle: "Popular upgrades",
      scope: [
        "Deadlocks and high-security cylinders on entry doors",
        "Window locks throughout — often an insurance requirement",
        "Sliding and ranchslider door locks and bolts",
        "Smart locks and keyless entry, set up and demonstrated",
        "Garage, side-gate and shed hardware",
        "Home safes — supplied, positioned and bolted down properly",
      ],
      showRates: true,
      image: "/images/work/digital-lock-outside.jpeg",
      imageAlt: "Digital door lock installed by Shore Doors — exterior side",
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/residential/lock-repair-replacement", label: "Lock repair & replacement" },
        { to: "/commercial/access-control-installation", label: "Access control (commercial)" },
      ],
    },
  },

  commercial: {
    "commercial-locksmith": {
      slug: "commercial-locksmith",
      gated: true,
      title: "Commercial Locksmith Services",
      metaTitle: "Commercial Locksmith Auckland | Shore Doors Locksmiths",
      metaDescription:
        "Business lockouts, rekeying after staff changes, restricted keys and shopfront hardware — from the installers Auckland's security companies already use.",
      heroSub:
        "Lockouts, rekeys and key control for businesses, landlords and property managers — delivered by the team that installs commercial hardware every day.",
      intro:
        "Staff change, keys go missing, tenants move on. We rekey and re-secure commercial premises with minimal disruption, keep key control tidy with restricted profiles, and fix the hardware problems we find while we're there — because commercial door hardware is our core trade.",
      scopeTitle: "What we handle",
      scope: [
        "Business lockouts and urgent re-securing",
        "Rekeying after staff or tenancy changes",
        "Restricted key profiles and key management",
        "Shopfront, office and back-of-house lock hardware",
        "Landlord and property-manager support across portfolios",
      ],
      image: "/images/stock/commercial-locksmith-banner.jpg",
      imageAlt: "Commercial locksmithing services — Shore Doors Locksmiths",
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/commercial/master-key-systems", label: "Master key systems" },
        { to: "/commercial/maintenance-iqp", label: "Maintenance & IQP support" },
      ],
    },
    "door-hardware-installation": {
      slug: "door-hardware-installation",
      gated: false,
      title: "Commercial Door Hardware Installation",
      metaTitle: "Commercial Door Hardware Installation Auckland | Shore Doors Locksmiths",
      metaDescription:
        "Mortice locks, panic hardware, door closers and fire-door hardware installed to NZ Building Code by Auckland's specialist install team.",
      heroSub:
        "Commercial mortice locks, panic and exit hardware, closers and fire-door hardware — installed for durability, compliance and minimal callbacks.",
      intro:
        "This is the work Shore Doors is known for. We install and service commercial-grade mechanical door hardware built to withstand daily use in demanding environments — hospitals, schools, government buildings, corporate offices. New fit-out or replacement programme, the hardware is selected and installed for long-term reliability, with fire-door and Building Code requirements handled properly.",
      scopeTitle: "Installation scope",
      scope: [
        "Commercial mortice locks, cylinders and levers",
        "Panic and exit hardware for compliant egress",
        "Door closers and door control hardware",
        "Fire-door hardware to code and insurance requirements",
        "Hinges, seals and door furniture",
        "Swing, sliding and gate automation",
      ],
      image: "/images/work/crashbar-kickplates.jpeg",
      imageAlt: "Crash bar with kick plates installed by Shore Doors",
      video: "/videos/gate-closer-maglock-install.mp4",
      videoCaption: "Heavy-duty gate closer installed on a heavy steel gate, with maglock — one of ours.",
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/commercial/access-control-installation", label: "Access control installation" },
        { to: "/commercial/maintenance-iqp", label: "Maintenance & IQP support" },
      ],
    },
    "access-control-installation": {
      slug: "access-control-installation",
      gated: false,
      title: "Access Control Hardware Installation",
      metaTitle: "Access Control Installation Auckland | Shore Doors Locksmiths",
      metaDescription:
        "Maglocks, electric strikes, drop bolts, Aperio wireless locks, cabling and commissioning — installed to your integrator's specification. Auckland-wide delivery partner.",
      heroSub:
        "The locking layer of access control — maglocks, electric strikes, drop bolts, electronic mortice locks and wireless locks — installed clean and commissioned properly. (Readers and system programming stay with your integrator.)",
      intro:
        "We install the hardware that makes access control systems work, to the specification set by your integrator or project team. That includes low-voltage cabling and power with battery backup, full termination and labelling, and testing and handover documentation for every access point. We work alongside your access control provider — supporting your project team, not replacing them.",
      scopeTitle: "Installation scope",
      scope: [
        "Maglocks, electric strikes and drop bolts",
        "Electronic mortice locks and wireless locks (e.g. Aperio)",
        "Low-voltage cabling, power supplies and battery backup",
        "Commissioning, testing and as-built handover documentation",
        "New builds and retrofit projects",
      ],
      image: "/images/work/double-maglock-hospital.jpeg",
      imageAlt: "Double maglock installed into a hospital double-door frame by Shore Doors",
      video: "/videos/v-lock-glass-doors-install.mp4",
      videoCaption: "Electronic V-Lock installation on commercial glass double doors.",
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/partners", label: "Work with us as your install partner" },
        { to: "/commercial/door-hardware-installation", label: "Door hardware installation" },
      ],
    },
    "door-automation": {
      slug: "door-automation",
      gated: false,
      title: "Door Automation & Accessibility — AHS A200",
      metaTitle: "Automatic Door Openers Auckland — Certified AHS A200 Installer | Shore Doors Locksmiths",
      metaDescription:
        "Auckland's only business certified to install and service the AHS A200 door actuator. Automatic door opening for accessibility — regularly delivered for disabled customers through ACC.",
      heroSub:
        "Automatic door opening for accessibility and convenience. We are the only business in Auckland certified to install and service the AHS A200 door actuator — and we fit them regularly for disabled customers through ACC.",
      intro:
        "An automatic door can be the difference between needing help and having independence. We supply, install and service low-energy swing door operators — led by the AHS A200 door actuator, which we're uniquely certified for in Auckland — for homes, healthcare facilities and businesses. For disabled customers, this is work we regularly deliver through ACC-supported home modifications, working in with assessors and occupational therapists.",
      scopeTitle: "What we deliver",
      scope: [
        "AHS A200 door actuator — certified supply, installation and servicing (Auckland's only certified business)",
        "Automatic door openers for wheelchair users and accessibility — homes and workplaces",
        "ACC-supported accessibility installs, coordinated with assessors and OTs",
        "Activation options to suit: push pads, remotes and hands-free operation",
        "Swing door operators, sliding doors and gate automation for commercial sites",
        "Automatic door repair and ongoing servicing to keep openers safe, compliant and reliable",
      ],
      image: "/images/work/a200-custom-paint.jpeg",
      imageAlt: "AHS A200 door actuator with custom paint finish, installed by Shore Doors",
      heroImageRight: true, // small source image — right 60% of the banner with the standard left fade
      videos: [
        { src: "/videos/a200-door-opener-demo.mp4", caption: "The AHS A200 door opener in action — from one of our installs." },
        { src: "/videos/a200-swing-door-2.mp4", caption: "Another AHS A200 swing-door install." },
        { src: "/videos/a200-push-and-go-function.mp4", caption: "Push & Go on a retirement-village fire door: the mechanical lock stays, the operator assists the opening." },
      ],
      primaryCta: callCta,
      crossLinks: [
        { to: "/commercial/door-hardware-installation", label: "Door hardware installation" },
        { to: "/residential/home-security-upgrades", label: "Home security upgrades" },
      ],
    },
    "master-key-systems": {
      slug: "master-key-systems",
      gated: true,
      title: "Master Key Systems",
      metaTitle: "Master Key Systems Auckland | Shore Doors Locksmiths",
      metaDescription:
        "Master key and restricted key systems designed, installed and managed for schools, offices, body corporates and property portfolios across Auckland.",
      heroSub:
        "One key for management, the right keys for everyone else — designed, documented and kept under control with restricted profiles.",
      intro:
        "A good key system means people open exactly what they should and nothing they shouldn't, and lost keys stop being a crisis. We design master key hierarchies around how your site actually runs, build them on restricted keyways so keys can't be copied at a kiosk, and keep a proper key register so control survives staff turnover.",
      scopeTitle: "What we deliver",
      scope: [
        "Master key system design for new and existing sites",
        "Restricted keyways — copies only on your written authority",
        "Keyed-alike and sub-master structures for departments and tenancies",
        "Key registers and signing procedures",
        "Staged rollouts that work around your occupants",
      ],
      image: "/images/projects/auckland-city-hospital.webp",
      imageAlt: "Auckland City Hospital — key systems across multiple areas",
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/commercial/commercial-locksmith", label: "Commercial locksmith services" },
        { to: "/commercial/maintenance-iqp", label: "Maintenance & IQP support" },
      ],
    },
    "maintenance-iqp": {
      slug: "maintenance-iqp",
      gated: false,
      title: "IQP Inspections, BWOF Support & Door Maintenance",
      metaTitle: "IQP Door Inspections & BWOF Maintenance Auckland | Shore Doors Locksmiths",
      metaDescription:
        "IQP inspections for automatic doors, fire doors and exit hardware, Building Warrant of Fitness (BWOF) support and scheduled door maintenance for Auckland facilities teams.",
      heroSub:
        "Keep doors, automatic openers and access-control hardware working and compliant — scheduled maintenance, IQP inspections and Building Warrant of Fitness (BWOF) support, plus fast remedials.",
      intro:
        "Doors are the highest-wear moving parts in most buildings — and several of them are specified systems that have to stay compliant. We provide scheduled preventive maintenance alongside your integrator and facilities team, carry out IQP inspections and support your annual Building Warrant of Fitness (BWOF) for the door-related specified systems we cover — automatic doors, fire and smoke doors and their hardware, and final exit doors — and handle remedial work on failed or underperforming hardware before it becomes a compliance or security problem. Planned maintenance agreements available for single sites or portfolios.",
      scopeTitle: "Programme scope",
      scope: [
        "Scheduled preventive maintenance visits",
        "IQP inspections and Building Warrant of Fitness (BWOF) support",
        "Automatic door servicing and safety checks",
        "Fire-door, smoke-door and final-exit hardware inspection and remedials",
        "Fault-finding on access hardware, door closers and panic/exit devices",
        "Condition reporting across property portfolios",
      ],
      image: "/images/projects/papakura-library.webp",
      imageAlt: "Papakura Library — door hardware maintained by Shore Doors",
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/commercial/door-hardware-installation", label: "Door hardware installation" },
        { to: "/partners", label: "Trade partners" },
      ],
    },
  },
};

// Recent-work photo strip on /projects — real install photos from the team
export const workGallery = [
  { src: "/images/work/black-maglock-custom-cable-box.jpeg", caption: "Commercial maglock — custom aluminium bracket and cover plate we fabricated to conceal the ceiling cable run" },
  { src: "/images/work/gate-maglock-closed.jpeg", caption: "Custom atrium gate — maglock flush-mounted by our team" },
  { src: "/images/work/gate-maglock-flushmounted.jpeg", caption: "Same gate: maglock recessed flush into the post" },
  { src: "/images/work/gate-maglock-armature.jpeg", caption: "Same gate: armature plate detail" },
  { src: "/images/work/pinpad-steel-gate.jpeg", caption: "Pinpad lock on a steel gate with guard plate" },
  { src: "/images/work/electronic-sliding-door-hook-lock.jpeg", caption: "Electronic hook lock on a sliding door" },
  { src: "/images/work/strike-lock-wooden-frame.jpeg", caption: "Electric strike fitted to a wooden frame" },
  { src: "/images/work/double-maglock-hospital.jpeg", caption: "Double maglock installed into a hospital double-door frame" },
  { src: "/images/work/cavity-slider-edge.jpeg", caption: "Residential cavity slider — mortice edge detail" },
  { src: "/images/work/digital-lock-inside.jpeg", caption: "Residential digital lock (Schlage) — mortice edge detail" },
  { src: "/images/work/crashbar-kickplates.jpeg", caption: "Crash bar with kick plates" },
  { src: "/images/work/a200-custom-paint.jpeg", caption: "AHS A200 door actuator — custom paint to match the joinery" },
];

// Hub card listings (order = display order)
// bg = faded card background — Hugh's chosen images (5 Jul 2026)
export const residentialCards = [
  { slug: "lock-repair-replacement", blurb: "Sticking, worn or broken locks repaired or replaced with quality hardware.", icon: "Wrench", bg: "/images/stock/deadbolt-installation.png" },
  { slug: "home-security-upgrades", blurb: "Deadlocks, window locks, sliding-door locks and smart locks — fitted properly.", icon: "Shield", bg: "/images/stock/home-security-digital-lock.png" },
  { slug: "lock-rekeying", blurb: "New keys, same locks — after moving house, lost keys or a tenancy change.", icon: "KeyRound", bg: "/images/stock/rekeying-cylinder-keys.png" },
  { slug: "key-cutting", blurb: "Keys cut at your place from our mobile van.", icon: "Key", bg: "/images/stock/key-cutting-man.webp" },
];

export const commercialCards = [
  { slug: "door-hardware-installation", blurb: "Mortice locks, panic hardware, closers and fire-door hardware to code.", icon: "DoorOpen" },
  { slug: "access-control-installation", blurb: "Maglocks, electric strikes, drop bolts and wireless locks, installed to your integrator's spec.", icon: "Lock" },
  { slug: "door-automation", blurb: "Automatic door openers — Auckland's only certified AHS A200 installer; accessibility work through ACC.", icon: "Accessibility" },
  { slug: "maintenance-iqp", blurb: "Scheduled maintenance, IQP support and remedials for facilities teams.", icon: "Settings" },
  { slug: "master-key-systems", blurb: "Restricted master key systems designed and kept under control.", icon: "KeyRound" },
  { slug: "commercial-locksmith", blurb: "Lockouts, rekeys and key control for businesses and property managers.", icon: "Building2" },
];
